import { useCallback, useState } from 'react';
import { AlertTriangle, BarChart3, ChevronRight, Copy, Gift, IdCard, ListChecks, ShieldCheck } from 'lucide-react';
import '../../../../shared/components/ui/Shared.css';
import './StorePortal.css';
import ErrorBoundary from '../../../../shared/components/ErrorBoundary';
import useStoreAuth from '../../hooks/useStoreAuth';
import useForgotPassword from '../../hooks/useForgotPassword';
import RegisterForm from '../../components/RegisterForm';
import LoginForm from '../../components/LoginForm';
import ForgotPasswordModal from '../../components/ForgotPasswordModal';

const StorePortal = ({
  storeRegister,
  storeLogin,
  onStoreRegisterFieldChange,
  onStoreLoginFieldChange,
  onRegister,
  onLogin,
  onGoogleAuthSuccess,
  request,
  onToast,
  authToken,
  mode = 'both'
}) => {
  const showRegister = mode !== 'login';
  const showLogin = mode !== 'register';
  const isLoginOnly = mode === 'login';
  const isRegisterOnly = mode === 'register';
  const gridClassName = showRegister && showLogin ? 'grid two' : (isLoginOnly ? 'grid login-grid' : 'grid');
  const sectionClassName = isLoginOnly
    ? 'section auth-single-shell login-only'
    : isRegisterOnly
      ? 'section auth-single-shell register-only'
      : 'section';

  const [activeRequestCount, setActiveRequestCount] = useState(0);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const runWithGlobalLoader = useCallback(async (executor) => {
    setActiveRequestCount((prev) => prev + 1);
    try {
      return await executor();
    } finally {
      setActiveRequestCount((prev) => Math.max(0, prev - 1));
    }
  }, []);

  const useStoreAuthResult = useStoreAuth({
    showRegister,
    request,
    storeRegister,
    onStoreRegisterFieldChange,
    onToast,
    runWithGlobalLoader
  });

  const forgotPassword = useForgotPassword({
    request,
    onToast,
    runWithGlobalLoader
  });

  const handleRegisterWrapper = async () => {
    setIsRegisterLoading(true);
    try {
      await runWithGlobalLoader(() => onRegister());
      setShowConfirmModal(false);
    } finally {
      setIsRegisterLoading(false);
    }
  };

  const handleLoginWrapper = async () => {
    setIsLoginLoading(true);
    try {
      await runWithGlobalLoader(() => onLogin());
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleOpenForgotPassword = () => {
    forgotPassword.reset();
    setShowForgotPassword(true);
  };

  const handleCloseForgotPassword = () => {
    setShowForgotPassword(false);
    forgotPassword.reset();
  };

  const isPageLoading = activeRequestCount > 0;
  const selectedMall = useStoreAuthResult.selectedMall;
  const authHighlights = [
    {
      icon: Gift,
      title: 'Offers that bring people back',
      description: 'Launch targeted discounts and campaigns in a few taps, and see exactly which ones drove footfall.'
    },
    {
      icon: IdCard,
      title: 'Memberships are digital',
      description: 'Replace paper punch cards with memberships customers carry in their pocket. New sign-ups in seconds.'
    },
    {
      icon: ListChecks,
      title: 'Staff and roles, handled',
      description: 'Give every employee the right access, track who did what, manage the whole team from one screen.'
    },
    {
      icon: BarChart3,
      title: 'Analytics that matter',
      description: 'Footfall, wait times, repeat-visit rate and revenue, finally in one place.'
    }
  ];

  const loginForm = showLogin ? (
    <LoginForm
      storeLogin={storeLogin}
      onStoreLoginFieldChange={onStoreLoginFieldChange}
      onSuccess={handleLoginWrapper}
      onToast={onToast}
      request={request}
      onGoogleAuthSuccess={onGoogleAuthSuccess}
      isLoading={isLoginLoading}
      onOpenForgotPassword={handleOpenForgotPassword}
      showRegister={showRegister}
      onScrollToRegister={() => {
        const registerSection = document.querySelector('.card:first-child');
        registerSection?.scrollIntoView({ behavior: 'smooth' });
      }}
    />
  ) : null;

  const registerForm = showRegister ? (
    <RegisterForm
      storeRegister={storeRegister}
      onStoreRegisterFieldChange={onStoreRegisterFieldChange}
      onSuccess={handleRegisterWrapper}
      onToast={onToast}
      request={request}
      authToken={authToken}
      useStoreAuthResult={useStoreAuthResult}
      runWithGlobalLoader={runWithGlobalLoader}
      isLoading={isRegisterLoading}
      selectedMall={selectedMall}
      onRegisterClick={() => setShowConfirmModal(true)}
    />
  ) : null;

  return (
    <ErrorBoundary level="feature">
      <section id="store-portal" className={sectionClassName} aria-busy={isPageLoading}>
        {isLoginOnly || isRegisterOnly ? (
          <div className="login-page-shell">
            <aside className="login-marketing-panel">
              <div className="login-hero-copy">
                <h1>Turn first visits into regulars.</h1>
                <p>The operating system that runs your store and bring people back.</p>
              </div>

              <div className="login-highlights">
                {authHighlights.map(({ icon: Icon, title, description }) => (
                  <div key={title} className="login-highlight-item">
                    <span className="login-highlight-icon" aria-hidden="true">
                      <Icon size={18} />
                    </span>
                    <div className="login-highlight-copy">
                      <h3>{title}</h3>
                      <p>{description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="login-marketing-footer">
                <div className="login-marketing-links">
                  <a href="/support">Trouble signing in?</a>
                  <a href="/support">Contact support</a>
                </div>
              </div>
            </aside>

            <div className="login-auth-stage">
              <div className="login-stage-topbar">
                {isLoginOnly ? (
                  <>
                    <span>New to Zopeo?</span>
                    <a href="/register" className="login-stage-cta">
                      Create a store
                      <ChevronRight size={16} />
                    </a>
                  </>
                ) : (
                  <>
                    <span>Already have a store?</span>
                    <a href="/login" className="login-stage-cta">
                      Sign in
                      <ChevronRight size={16} />
                    </a>
                  </>
                )}
              </div>

              <div className="login-stage-center">
                <div className={`login-stage-form${isRegisterOnly ? ' login-stage-form--register' : ''}`}>
                  {isRegisterOnly ? registerForm : loginForm}
                </div>
              </div>

              <div className="login-stage-footer">
                {isLoginOnly ? (
                  <>
                    <span>Don't have a store yet?</span>
                    <a href="/register">
                      Register your store
                      <ChevronRight size={16} />
                    </a>
                  </>
                ) : (
                  <>
                    <span>Already registered?</span>
                    <a href="/login">
                      Go to sign in
                      <ChevronRight size={16} />
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className={gridClassName}>
            {registerForm}
            {loginForm}
          </div>
        )}

        {showForgotPassword && (
          <ForgotPasswordModal
            forgotPassword={forgotPassword}
            onClose={handleCloseForgotPassword}
          />
        )}

        {showConfirmModal && (
          <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
            <div className="modal-content" onClick={(event) => event.stopPropagation()}>
              <h3>Confirm Store Registration</h3>
              <p>
                {storeRegister.mallUuid && selectedMall ? (
                  <>
                    You're registering a store at <strong>{selectedMall.mallName}</strong> {selectedMall.area ? `(${selectedMall.area}, ` : '('}{selectedMall.city}, {selectedMall.state} - {selectedMall.pincode}).
                  </>
                ) : (
                  <>
                    You're registering an <strong>autonomous store</strong> in {storeRegister.area ? `${storeRegister.area}, ` : ''}{storeRegister.city}, {storeRegister.state} - {storeRegister.pincode}.
                  </>
                )}
              </p>

              <div className="store-code-wrapper">
                <input className="input" value={storeRegister.storeCode} disabled />
                <button
                  type="button"
                  className="copy-btn"
                  title="Copy store code"
                  onClick={() => {
                    navigator.clipboard.writeText(storeRegister.storeCode);
                    onToast?.({ message: 'Store code copied to clipboard!', variant: 'success' });
                  }}
                >
                  <Copy size={16} />
                </button>
              </div>

              <div className="importance-notice">
                <div className="importance-icon"><AlertTriangle size={16} /></div>
                <div className="importance-text">
                  <h6>Store Code is critical</h6>
                  <p>Please copy and save this code securely. It is <strong>required</strong> for all your future logins.</p>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn ghost" onClick={() => setShowConfirmModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn primary" onClick={() => {
                  setShowConfirmModal(false);
                  handleRegisterWrapper();
                }}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {isPageLoading && (
          <div className="global-request-overlay" aria-live="polite" aria-label="Syncing data">
            <div className="global-request-loader">
              <span className="global-request-spinner" />
              <p>Syncing data...</p>
            </div>
          </div>
        )}
      </section>
    </ErrorBoundary>
  );
};

export default StorePortal;
